/**
 * SafetyAttestationOnboardingBanner.jsx — admin-only onboarding nudge.
 *
 * Shown on the admin Dashboard the first time a super-user logs in for a
 * company that has never reviewed the auto-generated OSHA / OH&S safety
 * attestation. Two paths:
 *
 *   1. "Review checklist" → routes to Settings → Compliance for full edit.
 *   2. "Use OSHA defaults" → marks the attestation as reviewed in place so
 *      the banner dismisses without forcing the admin to open Settings.
 *
 * Once `reviewed_at` is set on the company (either by saving items or by
 * dismissing here) the banner never reappears. Workers are unaffected —
 * this only renders for `isAdmin`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ShieldCheck, X, ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

export default function SafetyAttestationOnboardingBanner({ isAdmin }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/company/safety-attestation`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.reviewed_at) {
          setItemCount((data?.items || []).length);
          setVisible(true);
        }
      })
      .catch(() => { /* silent — banner just won't show */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!visible) return null;

  const markReviewed = async () => {
    setWorking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/company/safety-attestation`,
        { reviewed: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Safety attestation acknowledged',
        description: 'You can revisit and customise the checklist anytime under Settings → Compliance.',
      });
      setVisible(false);
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="relative rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-slate-900/40 to-slate-900/60 p-4 sm:p-5 shadow-lg"
      data-testid="safety-onboarding-banner"
    >
      <button
        type="button"
        onClick={markReviewed}
        disabled={working}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-100 transition-colors"
        aria-label="Dismiss"
        data-testid="safety-onboarding-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-white">
            Review your Safety Attestation checklist
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 mt-1 leading-relaxed">
            Hines Time pre-loaded <span className="font-semibold text-emerald-300">{itemCount}</span>{' '}
            OSHA / OH&amp;S fit-for-duty items that every worker must acknowledge before starting a
            job. Take 30 seconds to tailor the wording to your trade — or accept the defaults if
            they fit your shop.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => navigate('/settings?section=safety-attestation')}
              disabled={working}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="safety-onboarding-review-btn"
            >
              Review checklist <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={markReviewed}
              disabled={working}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="safety-onboarding-accept-defaults-btn"
            >
              {working ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Looks good — use defaults
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
